import {
    Container,
    Stack,
    Typography,
    Fab,
    Card,
    CardContent,
    Box,
    Breadcrumbs,
    Link,
    CircularProgress,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { useAuth, AuthStatus } from '../auth/Auth';
import { useCallback, useEffect, useRef, useState } from 'react';
import { SubscriptionStatus } from '../database/user';
import { Topic } from '../database/forum';
import TopicCard from './TopicCard';
import CreateTopicDialog from './CreateTopicDialog';
import { useRequest } from '../api/Request';
import { useApi } from '../api/Api';
import { CreateTopicRequest, ListTopicsResponse } from '../api/forumApi';
import LoadMoreButton from '../bts/components/LoadMoreButton';
import { useHasForumAccess } from './useHasForumAccess';
import LockedContentOverlay from './LockedContentOverlay';
import { CommunityThemeProvider } from '../ThemeProvider';

const ForumDetailPage = () => {
    const auth = useAuth();
    const navigate = useNavigate();
    const api = useApi();
    const listTopicsRequest = useRequest();
    const [data, setData] = useState<ListTopicsResponse>();
    const [loading, setLoading] = useState(true);
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [newTopicSk, setNewTopicSk] = useState<string>();
    const topicRefs = useRef<Map<string, HTMLDivElement | null>>(new Map());
    const hasForumAccess = useHasForumAccess();
    const { forumNameAndSk } = useParams<{ forumNameAndSk: string }>();
    const [searchParams, setSearchParams] = useSearchParams();
    const linkedTopicSk = searchParams.get('topic') || undefined;
    const [highlightedTopicSk, setHighlightedTopicSk] = useState<string>();
    const [linkedTopicData, setLinkedTopicData] = useState<Topic>();
    const linkedTopicFetched = useRef(false);

    // Parse forum name and SK from URL
    // React Router automatically decodes the URL parameter
    const separator = '-FORUM#';
    const splitIndex = forumNameAndSk?.lastIndexOf(separator) ?? -1;
    const forumName =
        splitIndex > 0 ? forumNameAndSk?.substring(0, splitIndex) : undefined;
    const sk = splitIndex > 0 ? forumNameAndSk?.substring(splitIndex + 1) : undefined; // Skip just the dash, keep "FORUM#..."

    const handleResponse = useCallback(
        (resp: ListTopicsResponse) => {
            // Create a Set of existing forum IDs for fast lookup
            const existingIds = new Set((data?.topics || []).map((t) => t.sk));

            // Filter out any forums we already have
            const newForums = resp.topics.filter((t) => !existingIds.has(t.sk));

            const allTopics = (data?.topics || []).concat(newForums);

            // Sort by creation date (newest first)
            allTopics.sort((lhs, rhs) => rhs.createdAt.localeCompare(lhs.createdAt));

            setData({
                topics: allTopics,
                lastEvaluatedKey: resp.lastEvaluatedKey,
            });
            setLoading(false);
        },
        [data, setData],
    );

    useEffect(() => {
        if (!forumNameAndSk || !forumName || !sk) {
            navigate('/ess');
            return;
        }

        if (!listTopicsRequest.isSent()) {
            api.listTopics(sk)
                .then((resp) => {
                    handleResponse(resp.data);
                    listTopicsRequest.onSuccess(resp);
                })
                .catch((error) => {
                    console.error('Failed to load topics:', error);
                    setLoading(false);
                    listTopicsRequest.onFailure(error);
                });
        }
    }, [forumNameAndSk, forumName, sk, listTopicsRequest, api, handleResponse, navigate]);

    // Scroll to newly created topic
    useEffect(() => {
        if (newTopicSk) {
            const topicElement = topicRefs.current.get(newTopicSk);
            if (topicElement) {
                topicElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                setNewTopicSk(undefined);
            }
        }
    }, [newTopicSk, data]);

    // Deep-link: fetch linked topic if not in loaded list
    useEffect(() => {
        if (!linkedTopicSk || !data || linkedTopicFetched.current) return;

        const found = data.topics.find((t) => t.sk === linkedTopicSk);
        if (found) {
            // Topic is already loaded — scroll to it
            setHighlightedTopicSk(linkedTopicSk);
            setTimeout(() => setHighlightedTopicSk(undefined), 5000);
            setTimeout(() => {
                const el = topicRefs.current.get(linkedTopicSk);
                if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }, 100);
            return;
        }

        // Not in loaded list — fetch it
        linkedTopicFetched.current = true;
        api.getTopic(sk!, linkedTopicSk)
            .then((resp) => {
                setLinkedTopicData(resp.data);
                setHighlightedTopicSk(linkedTopicSk);
                setTimeout(() => setHighlightedTopicSk(undefined), 5000);
            })
            .catch((err) => {
                console.error('Failed to fetch linked topic:', err);
            });
    }, [linkedTopicSk, data, api]);

    // Scroll to fetched linked topic once it renders
    useEffect(() => {
        if (linkedTopicData && highlightedTopicSk) {
            setTimeout(() => {
                const el = topicRefs.current.get(highlightedTopicSk);
                if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }, 100);
        }
    }, [linkedTopicData, highlightedTopicSk]);

    const clearLinkedTopic = () => {
        setHighlightedTopicSk(undefined);
        setLinkedTopicData(undefined);
        linkedTopicFetched.current = false;
        setSearchParams((prev) => {
            prev.delete('topic');
            return prev;
        });
    };

    const handleResponseLoadMore = useCallback(
        (resp: ListTopicsResponse) => {
            const newEntries = (data?.topics || []).concat(resp.topics);

            setData({
                topics: newEntries,
                lastEvaluatedKey: resp.lastEvaluatedKey,
            });
        },
        [data],
    );

    // Early return after all hooks
    if (!forumNameAndSk || !forumName || !sk) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '200px',
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    const onLoadMore = () => {
        listTopicsRequest.onStart();
        api.listTopics(sk, data?.lastEvaluatedKey || '')
            .then((resp) => {
                handleResponseLoadMore(resp.data);
                listTopicsRequest.onSuccess(resp.data);
            })
            .catch((err) => {
                console.error('listTopics: ', err);
                listTopicsRequest.onFailure(err);
            });
    };

    // Check if user can create topics (authenticated and subscribed)
    const canCreateTopic =
        auth.status === AuthStatus.Authenticated &&
        auth.user !== undefined &&
        (auth.user.subscriptionStatus === SubscriptionStatus.SUBSCRIBED ||
            auth.user.subscriptionOverride ||
            auth.user.isArtist);
    console.log('canCreateTopic:', canCreateTopic);

    const handleCreateTopic = async (req: CreateTopicRequest, file?: File) => {
        api.createTopic(sk, req)
            .then(async (resp) => {
                // If there's a file, wait for upload to complete before updating UI
                if (file) {
                    try {
                        // Generate presigned URL
                        const presignedResp = await api.putPresignedUrl(
                            sk,
                            resp.data.topic.sk,
                            resp.data.threadId,
                            file.type,
                        );

                        // Upload file and wait for completion
                        await api.uploadMediaToS3({
                            presignedUrl: presignedResp.data.presignedUrl,
                            contentType: file.type,
                            mediaContent: file,
                        });
                    } catch (error) {
                        console.error('Failed to upload media:', error);
                        // Still add topic to UI even if media upload fails
                    }
                }

                // Add topic to UI after media upload completes (or if no media)
                setData((prevData) => {
                    const updatedTopics = prevData
                        ? [resp.data.topic, ...prevData.topics]
                        : [resp.data.topic];
                    return {
                        topics: updatedTopics,
                        lastEvaluatedKey: prevData?.lastEvaluatedKey,
                    };
                });

                // Set the new topic SK for scrolling
                setNewTopicSk(resp.data.topic.sk);
                setCreateDialogOpen(false);
            })
            .catch((error) => {
                console.error('Failed to create topic:', error);
            });
    };

    const handleDeleteTopic = (i: number) => {
        const newData = data?.topics || [];
        setData({
            topics: [...newData.slice(0, i), ...newData.slice(i + 1)],
            lastEvaluatedKey: data?.lastEvaluatedKey || '',
        });
    };

    if (auth.status === AuthStatus.Loading || loading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '200px',
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <CommunityThemeProvider>
        <Box
            sx={(theme) => ({
                minHeight: '100vh',
                bgcolor: 'background.default',
                background: `radial-gradient(ellipse at 50% 0%, ${theme.palette.primary.main}08 0%, #060f1e 45%, #030810 100%)`,
            })}
        >
        <Container maxWidth='md' sx={{ pt: 4, pb: 12 }}>
            <Stack spacing={3}>
                {/* Breadcrumb Navigation */}
                <Breadcrumbs aria-label='breadcrumb' color='secondary'>
                    <Link
                        component='button'
                        variant='h6'
                        underline='always'
                        fontFamily='LemonMilk'
                        color='secondary'
                        onClick={() => navigate('/ess')}
                        sx={{
                            cursor: 'pointer',
                        }}
                    >
                        Forums
                    </Link>
                    <Typography variant='h6' fontFamily='LemonMilk' color='secondary'>
                        {forumName}
                    </Typography>
                </Breadcrumbs>

                {/* Topics list */}
                {!data || data.topics.length === 0 ? (
                    <Card>
                        <CardContent>
                            <Typography variant='body1' textAlign='center'>
                                No topics yet. Be the first to start a discussion!
                            </Typography>
                        </CardContent>
                    </Card>
                ) : (
                    <LockedContentOverlay
                        locked={!hasForumAccess}
                        message='Subscribe to unlock!'
                        blurAmount={4}
                    >
                        <Stack spacing={2}>
                            {/* Show separately-fetched linked topic at top */}
                            {linkedTopicData &&
                                !data.topics.find((t) => t.sk === linkedTopicData.sk) && (
                                    <Box
                                        key={linkedTopicData.sk}
                                        ref={(el: HTMLDivElement | null) => {
                                            if (el) {
                                                topicRefs.current.set(
                                                    linkedTopicData.sk,
                                                    el,
                                                );
                                            } else {
                                                topicRefs.current.delete(
                                                    linkedTopicData.sk,
                                                );
                                            }
                                        }}
                                    >
                                        <TopicCard
                                            topic={linkedTopicData}
                                            forumId={sk}
                                            forumName={forumName!}
                                            forumSk={sk!}
                                            onDelete={clearLinkedTopic}
                                            highlighted={true}
                                        />
                                    </Box>
                                )}
                            {data.topics.map((topic, i) => (
                                <Box
                                    key={topic.sk}
                                    ref={(el: HTMLDivElement | null) => {
                                        if (el) {
                                            topicRefs.current.set(topic.sk, el);
                                        } else {
                                            topicRefs.current.delete(topic.sk);
                                        }
                                    }}
                                >
                                    <TopicCard
                                        topic={topic}
                                        forumId={sk}
                                        forumName={forumName!}
                                        forumSk={sk!}
                                        onDelete={() => handleDeleteTopic(i)}
                                        highlighted={highlightedTopicSk === topic.sk}
                                    />
                                </Box>
                            ))}
                        </Stack>
                    </LockedContentOverlay>
                )}
                <LoadMoreButton
                    request={listTopicsRequest}
                    startKey={data?.lastEvaluatedKey || ''}
                    onLoadMore={onLoadMore}
                />
            </Stack>

            {/* Floating Action Button for creating topics */}
            {canCreateTopic && (
                <Fab
                    color='primary'
                    sx={{
                        position: 'fixed',
                        bottom: 80,
                        right: 16,
                    }}
                    onClick={() => setCreateDialogOpen(true)}
                >
                    <Add />
                </Fab>
            )}

            {/* Create Topic Dialog */}
            <CreateTopicDialog
                open={createDialogOpen}
                onClose={() => setCreateDialogOpen(false)}
                onSubmit={handleCreateTopic}
            />
        </Container>
        </Box>
        </CommunityThemeProvider>
    );
};

export default ForumDetailPage;
